import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  SkeletonRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { Company, CompanyCreateInput } from '@bia-auction/shared-schema';
import { companySchema } from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, Building2, Search } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { formatCount } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';

/**
 * Firms (partner companies) — the record an operator creates before a partner can send
 * anything, and the entry point to that firm's ingestion API keys (M13, ADR 0026). Until this
 * page existed, firms only came from the seed script and there was nowhere to issue a key.
 *
 * Deliberately mirrors `UsersPage`: one dialog for create + edit, ghost row actions, one
 * primary action in the header (docs/15 §8). The keys themselves live on the detail page —
 * a credential is not a row action.
 */

/** The fields the form collects; `null`s are sent so a cleared field actually clears. */
interface FirmFormValues {
  name: string;
  editorial: boolean;
  taxAdmin: string;
  taxNumber: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
}

/** Empty input → `null` (clear), not `''` — the API treats absent as "leave alone". */
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

function FirmForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial?: Company;
  onSubmit: (values: FirmFormValues) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [editorial, setEditorial] = useState(initial?.editorial ?? false);
  const [taxAdmin, setTaxAdmin] = useState(initial?.taxAdmin ?? '');
  const [taxNumber, setTaxNumber] = useState(initial?.taxNumber ?? '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ name, editorial, taxAdmin, taxNumber, contactEmail, contactPhone, address });
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="ff-name">{t('firms.field.name')}</Label>
        <Input id="ff-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="ff-tax-admin">{t('firms.field.taxAdmin')}</Label>
          <Input
            id="ff-tax-admin"
            value={taxAdmin}
            onChange={(e) => setTaxAdmin(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ff-tax-number">{t('firms.field.taxNumber')}</Label>
          <Input
            id="ff-tax-number"
            value={taxNumber}
            onChange={(e) => setTaxNumber(e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="ff-email">{t('firms.field.contactEmail')}</Label>
          <Input
            id="ff-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ff-phone">{t('firms.field.contactPhone')}</Label>
          <Input
            id="ff-phone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ff-address">{t('firms.field.address')}</Label>
        <Input id="ff-address" value={address} onChange={(e) => setAddress(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={editorial} onCheckedChange={(v) => setEditorial(v === true)} />
        {t('firms.field.editorial')}
      </label>
      <DialogFooter>
        <Button type="submit" disabled={submitting}>
          {t('common.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function FirmsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ firm?: Company } | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // Server-side search: the firm list is the picker's source too, so filtering belongs in the
  // query (and folds Turkish there) rather than in two places.
  const q = useDebounced(search.trim());

  const firms = useQuery({
    queryKey: ['companies', q, showArchived],
    queryFn: () =>
      api(
        `/admin/companies?${new URLSearchParams({
          ...(q ? { q } : {}),
          ...(showArchived ? { includeArchived: '1' } : {}),
        }).toString()}`,
        {},
        z.array(companySchema),
      ),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['companies'] });
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error');

  const create = useMutation({
    mutationFn: (body: CompanyCreateInput) =>
      api<Company>('/admin/companies', { method: 'POST', body }),
    onSuccess: () => {
      toast(t('firms.created'), 'success');
      setForm(null);
      void invalidate();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompanyCreateInput> }) =>
      api<Company>(`/admin/companies/${id}`, { method: 'PATCH', body: data }),
    onSuccess: () => {
      toast(t('firms.updated'), 'success');
      setForm(null);
      void invalidate();
    },
    onError: fail,
  });

  const submitForm = (v: FirmFormValues) => {
    const body = {
      name: v.name,
      editorial: v.editorial,
      taxAdmin: orNull(v.taxAdmin),
      taxNumber: orNull(v.taxNumber),
      contactEmail: orNull(v.contactEmail),
      contactPhone: orNull(v.contactPhone),
      address: orNull(v.address),
    };
    if (form?.firm) update.mutate({ id: form.firm.id, data: body });
    else create.mutate(body);
  };

  const rows = firms.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title={t('firms.title')}>
        <Button size="sm" onClick={() => setForm({})}>
          {t('firms.new')}
        </Button>
      </PageHeader>

      <div className="flex items-center gap-4">
        <div className="relative w-72">
          <Search aria-hidden className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={t('firms.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={showArchived}
            onCheckedChange={(v) => setShowArchived(v === true)}
          />
          {t('firms.showArchived')}
        </label>
      </div>

      {firms.isLoading ? (
        <SkeletonRows rows={6} />
      ) : firms.isError ? (
        <ErrorMsg error={firms.error} />
      ) : rows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('firms.col.name')}</TableHead>
              <TableHead>{t('firms.col.taxNumber')}</TableHead>
              <TableHead>{t('firms.col.contact')}</TableHead>
              <TableHead>{t('firms.col.keys')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((f) => (
              <TableRow key={f.id}>
                <TableCell className="space-x-2">
                  <Link
                    to="/firms/$companyId"
                    params={{ companyId: f.id }}
                    className="text-tx-brand hover:underline"
                  >
                    {f.name}
                  </Link>
                  {/* Archived is state: icon + word, never colour alone (docs/15 §2). */}
                  {f.archivedAt !== null && (
                    <Badge variant="secondary">
                      <Archive aria-hidden className="mr-1 h-3 w-3" />
                      {t('firms.archivedBadge')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{f.taxNumber ?? '—'}</TableCell>
                <TableCell>{f.contactEmail ?? f.contactPhone ?? '—'}</TableCell>
                <TableCell className="tabular-nums">{formatCount(f.activeKeyCount)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setForm({ firm: f })}>
                    {t('common.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState icon={Building2} title={t('firms.empty')} />
      )}

      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form?.firm ? t('firms.edit') : t('firms.new')}</DialogTitle>
          </DialogHeader>
          {form && (
            // Remount per subject so a previous edit's fields never leak into create.
            <FirmForm
              key={form.firm?.id ?? 'create'}
              initial={form.firm}
              submitting={create.isPending || update.isPending}
              onSubmit={submitForm}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
